"use client";

import type { RatingChip } from "@/lib/feedback/ratingTypes";

// Presentational input-row sub-components for RatingPrompt, extracted so the
// orchestrator stays under the changed-file LOC ceiling (guard G03). Pure,
// stateless — all state + submission logic lives in RatingPrompt.tsx.

interface WebStarsRowProps {
  stars: number;
  hoveredStar: number;
  onPress: (value: number) => void;
  onHover: (value: number) => void;
}

export function WebStarsRow({ stars, hoveredStar, onPress, onHover }: WebStarsRowProps) {
  return (
    <div style={{ display: "flex", gap: 2 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onPress(n)}
          onMouseEnter={() => onHover(n)}
          onMouseLeave={() => onHover(0)}
          aria-label={`${n} star`}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 2,
            fontSize: 14,
            color:
              n <= (hoveredStar || stars)
                ? "var(--ish-gold, var(--color-gold))"
                : "var(--ish-border)",
          }}
        >
          {"★"}
        </button>
      ))}
    </div>
  );
}

interface WebNpsRowProps {
  onPress: (value: number) => void;
}

// True 0–10 Net Promoter Score scale. 0 = Not likely … 10 = Very likely.
// Tapping a number submits that score immediately (rating=<0-10>).
export function WebNpsRow({ onPress }: WebNpsRowProps) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div
        role="group"
        aria-label="0 to 10 recommend scale"
        style={{ display: "flex", gap: 4, flexWrap: "wrap" }}
      >
        {Array.from({ length: 11 }, (_, n) => (
          <button
            key={n}
            type="button"
            onClick={() => onPress(n)}
            aria-label={`${n}`}
            style={{
              minWidth: 26,
              height: 26,
              padding: 0,
              borderRadius: 6,
              border: "1px solid var(--ish-border)",
              background: "transparent",
              color: "var(--color-dark)",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {n}
          </button>
        ))}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 10,
          color: "var(--ish-text-faint)",
        }}
      >
        <span>Not likely</span>
        <span>Very likely</span>
      </div>
    </div>
  );
}

interface WebChipRowProps {
  chips: RatingChip[];
  activeChipId: string | null;
  onPress: (value: string) => void;
}

export function WebChipRow({ chips, activeChipId, onPress }: WebChipRowProps) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {chips.map((chip) => (
        <WebChipButton
          key={chip.id}
          chip={chip}
          active={activeChipId === chip.id}
          onPress={() => onPress(chip.id)}
        />
      ))}
    </div>
  );
}

interface WebChipButtonProps {
  chip: RatingChip;
  active: boolean;
  onPress: () => void;
}

function WebChipButton({ chip, active, onPress }: WebChipButtonProps) {
  return (
    <button
      type="button"
      onClick={onPress}
      aria-pressed={active}
      style={{
        padding: "4px 10px",
        borderRadius: 99,
        border: `1px solid ${active ? "var(--ish-accent)" : "var(--ish-border)"}`,
        background: active ? "var(--ish-accent)" : "transparent",
        color: active ? "#fff" : "var(--color-dark)",
        fontSize: 11,
        fontWeight: 500,
        cursor: "pointer",
      }}
    >
      {chip.label}
    </button>
  );
}

interface WebThumbBtnProps {
  kind: "up" | "down";
  active: boolean;
  onPress: () => void;
}

export function WebThumbBtn({ kind, active, onPress }: WebThumbBtnProps) {
  const label = kind === "up" ? "Helpful" : "Not helpful";
  const color = active ? "var(--ish-accent)" : "var(--ish-text-faint)";
  return (
    <button
      type="button"
      onClick={onPress}
      aria-label={label}
      aria-pressed={active}
      style={{
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: 3,
        color,
        display: "flex",
        alignItems: "center",
      }}
      title={label}
    >
      {kind === "up" ? (
        <svg width="14" height="14" viewBox="0 0 256 256" fill="currentColor">
          <path d="M234,80.12A24,24,0,0,0,216,72H160V56a40,40,0,0,0-40-40,8,8,0,0,0-7.16,4.42L75.06,96H32a16,16,0,0,0-16,16v88a16,16,0,0,0,16,16H204a24,24,0,0,0,23.82-21l12-96A24,24,0,0,0,234,80.12ZM32,112H72v88H32Zm183.71,9.14-12,96A8,8,0,0,1,196,224H88V105.89l36.71-73.43A24,24,0,0,1,144,56V80a8,8,0,0,0,8,8h64a8,8,0,0,1,7.92,9.14Z" />
        </svg>
      ) : (
        <svg
          width="14"
          height="14"
          viewBox="0 0 256 256"
          fill="currentColor"
          style={{ transform: "rotate(180deg)" }}
        >
          <path d="M234,80.12A24,24,0,0,0,216,72H160V56a40,40,0,0,0-40-40,8,8,0,0,0-7.16,4.42L75.06,96H32a16,16,0,0,0-16,16v88a16,16,0,0,0,16,16H204a24,24,0,0,0,23.82-21l12-96A24,24,0,0,0,234,80.12ZM32,112H72v88H32Zm183.71,9.14-12,96A8,8,0,0,1,196,224H88V105.89l36.71-73.43A24,24,0,0,1,144,56V80a8,8,0,0,0,8,8h64a8,8,0,0,1,7.92,9.14Z" />
        </svg>
      )}
    </button>
  );
}
